import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { abrir, aparecer, pop, revelarDesde, subir, suave } from './movimiento';
import { useEsCelular } from '../lib/useMedia';
import { api, textoUbicacion, type Hallazgo } from '../api';
import { aviso, botonPrimario, botonSecundario, c, mono, tono } from '../theme';
import { Icono, useConfirmar } from './ui';
import { useEsAdmin } from '../lib/sesion';
import { mensajeDeError, useEscape } from '../lib/useApi';
import type { Vista } from './Layout';
import { avisoDeEscaneo, opcionesDeEscaneo, rutaOperar } from '../views/operar/desdeEscaneo';
import { GUIA, recorteGuia } from '../lib/recorteGuia';

/**
 * Resultado de una lectura hecha con la cámara (o de una búsqueda tipeada):
 * dice qué es la etiqueta y ofrece el próximo paso según dónde esté ese pallet
 * ahora mismo. Sólo ofrece lo que el rol puede abrir: un botón que lleva a una
 * pantalla prohibida termina en el inicio, que es peor que no tenerlo.
 */
export function ResultadoEscaneo({
  hallazgo,
  vistas,
  onIr,
  onEliminado,
}: {
  hallazgo: Hallazgo;
  vistas: string[];
  onIr: (v: Vista, id?: string) => void;
  /** El admin eliminó el pallet leído: el aviso que queda en lugar del panel. */
  onEliminado: (mensaje: string) => void;
}) {
  const esAdmin = useEsAdmin();
  const [confirmar, dialogo] = useConfirmar();
  const [eliminando, setEliminando] = useState(false);
  const [errorEliminar, setErrorEliminar] = useState('');
  // La casilla vive adentro del diálogo, que se desmonta al cerrarse: se lee de acá.
  const noExistia = useRef(false);

  // Sólo administración, y con motivo: lo que se borra queda en «Pallets
  // eliminados» para decidir después qué hacer con esa mercadería.
  async function eliminar() {
    setErrorEliminar('');
    setEliminando(true);
    noExistia.current = false;
    try {
      const { items } = await api.itemsDePallet(hallazgo.codigo);
      const fila = (rotulo: string, valor: string) => (
        <>
          <span style={{ color: c.texto3 }}>{rotulo}</span>
          <span style={{ color: c.texto, whiteSpace: 'pre-line' }}>{valor}</span>
        </>
      );
      const motivo = await confirmar({
        titulo: `¿Eliminar ${hallazgo.codigo} del sistema?`,
        detalle: (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
              {fila('Pallet', hallazgo.codigo)}
              {fila(
                'Producto',
                items.map((i) => `${i.articulo} · ${i.piezas} pz${i.lote ? ` · lote ${i.lote}` : ''}`).join('\n') || '—',
              )}
              {fila(
                'Contenedor',
                hallazgo.contenedor
                  ? `${hallazgo.contenedor}${hallazgo.contenedorNumero != null ? ` · N° ${hallazgo.contenedorNumero}` : ''}`
                  : 'Sin contenedor',
              )}
              {fila('Piezas', hallazgo.tipoPallet === 'completo' ? `${hallazgo.piezas ?? 0} (pallet completo)` : String(hallazgo.piezas ?? 0))}
              {fila('Ubicación', hallazgo.posicion ? textoUbicacion(hallazgo.posicion, hallazgo.nivel) : 'Sin ubicación')}
              {fila('Estado', hallazgo.estado ?? '—')}
            </div>
            <p style={{ margin: '10px 0 0' }}>No se puede deshacer. Queda registrado en Pallets eliminados.</p>
            {/* Sin esto las piezas vuelven a esperar pallet en Armado, y si esa
                mercadería no existía el contenedor queda «En descarga» para siempre. */}
            {hallazgo.contenedor && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, color: c.texto, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  defaultChecked={false}
                  onChange={(e) => (noExistia.current = e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  Este pallet no existía (cargado de más o duplicado): quitarlo también de lo declarado del contenedor. Si la
                  mercadería está, dejalo sin marcar y armale un pallet nuevo.
                </span>
              </label>
            )}
          </>
        ),
        boton: 'Eliminar pallet',
        motivo: 'Motivo',
      });
      if (motivo === null) return;
      const eliminado = await api.eliminarPallet(hallazgo.codigo, motivo);
      // No con `quitarDeclarado` del borrado: ése además se lleva las salidas
      // del pallet, entregadas incluidas, y desde el escáner eso no se quiere.
      if (noExistia.current && !eliminado.declaradoQuitado) await api.quitarDeclaradoDeEliminado(eliminado.id);
      onEliminado(
        `${hallazgo.codigo} se eliminó del sistema${noExistia.current ? ' y de lo declarado del contenedor' : ''}. Queda registrado en Pallets eliminados.`,
      );
    } catch (err) {
      setErrorEliminar(mensajeDeError(err));
    } finally {
      setEliminando(false);
    }
  }

  const t = hallazgo.tipo === 'desconocido' ? 'err' : hallazgo.tipo === 'recepcion' ? 'warn' : 'ok';
  const icono =
    hallazgo.tipo === 'recepcion'
      ? 'move_to_inbox'
      : hallazgo.tipo === 'pallet'
        ? 'inventory_2'
        : hallazgo.tipo === 'capacho'
          ? 'call_split'
          : hallazgo.tipo === 'articulo'
            ? 'barcode_scanner'
            : hallazgo.tipo === 'contenedor'
              ? 'directions_boat'
              : 'help';

  const todas: { texto: string; vista: Vista; id?: string }[] = [];
  if (hallazgo.tipo === 'pallet') {
    // Sin posición todavía: lo primero que hace falta es ubicarlo, y Ubicar
    // abre con ese pallet ya elegido. Con posición, lo que se busca es verlo
    // en el mapa.
    if (!hallazgo.ubicacion) {
      todas.push({ texto: 'Ubicar', vista: 'ubicar', id: hallazgo.codigo });
    } else {
      todas.push({ texto: 'Ver en el mapa', vista: 'racks', id: hallazgo.codigo });
    }
    todas.push({ texto: 'Ver ficha', vista: 'frac', id: hallazgo.codigo });
  }
  // El contenedor leído, o del que bajó el pallet: su ficha tiene todo lo demás.
  if ((hallazgo.tipo === 'contenedor' || hallazgo.tipo === 'pallet') && hallazgo.contenedor) {
    todas.push({ texto: 'Ver contenedor', vista: 'contenedor', id: hallazgo.contenedor });
  }
  // Lo leído fue el número de parte del material: lo que sigue es ver dónde
  // está guardado, que es para lo que se escanea una caja en el pasillo.
  if (hallazgo.tipo === 'articulo' && hallazgo.articulo) {
    todas.push({ texto: 'Ver en el mapa', vista: 'racks', id: hallazgo.articulo });
  }
  if (hallazgo.tipo === 'capacho' && hallazgo.palletOrigen) {
    todas.push({ texto: 'Ver pallet origen', vista: 'frac', id: hallazgo.palletOrigen });
  }
  // Primero, las operaciones guiadas que arrancan con lo leído ya cargado.
  const operar = vistas.includes('operar') ? opcionesDeEscaneo(hallazgo) : [];
  const acciones = [
    ...operar.map((o) => ({ texto: o.texto, vista: 'operar' as Vista, id: rutaOperar(o) })),
    ...todas.filter((a) => vistas.includes(a.vista) && !operar.some((o) => o.texto === a.texto)),
  ];
  const estado = avisoDeEscaneo(hallazgo);

  return (
    <div role={t === 'err' ? 'alert' : 'status'} style={{ ...aviso(t), display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
      <span
        style={{
          width: 38,
          height: 38,
          flex: '0 0 38px',
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          background: '#fff',
          border: `1px solid ${tono(t).bd}`,
          color: tono(t).fg,
        }}
      >
        <Icono nombre={icono} size={21} />
      </span>

      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontFamily: mono, fontSize: 12.5, color: c.texto3 }}>{hallazgo.codigo}</div>
        <div style={{ marginTop: 2, fontSize: 14, fontWeight: 600, color: c.texto }}>{hallazgo.titulo}</div>
        <div style={{ marginTop: 2, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>{hallazgo.detalle}</div>
        {estado && <div style={{ marginTop: 2, fontSize: 13, fontWeight: 600, color: c.texto }}>{estado}</div>}

        {acciones.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {acciones.map((a, i) => (
              // En el celular cada botón estira hasta llenar su renglón: sin
              // esto quedaban sueltos, cada uno de su ancho y con un hueco al lado.
              <button
                key={a.vista + a.texto}
                type="button"
                onClick={() => onIr(a.vista, a.id)}
                className="grow tablet:grow-0"
                style={{ ...(i < Math.max(operar.length, 1) ? botonPrimario : botonSecundario), minHeight: 44, padding: '9px 16px', justifyContent: 'center', fontSize: i < operar.length ? 15 : 13 }}
              >
                {a.texto}
              </button>
            ))}
          </div>
        )}

        {hallazgo.tipo === 'pallet' && esAdmin && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${tono(t).bd}` }}>
            <button
              type="button"
              onClick={eliminar}
              disabled={eliminando}
              className="w-full tablet:w-auto"
              style={{
                ...botonSecundario,
                justifyContent: 'center',
                minHeight: 40,
                padding: '7px 14px',
                fontSize: 13,
                border: `1px solid ${c.rojo}`,
                background: '#fff',
                color: c.rojo,
                opacity: eliminando ? 0.6 : 1,
              }}
            >
              <Icono nombre="delete_forever" size={18} />
              Eliminar del sistema
            </button>
            {errorEliminar && (
              <div role="alert" style={{ marginTop: 8, fontSize: 13, color: c.rojo, textWrap: 'pretty' }}>
                {errorEliminar}
              </div>
            )}
          </div>
        )}
        {dialogo}
      </div>
    </div>
  );
}

/**
 * Lector de códigos con la cámara del celular. La librería se carga sólo al
 * abrir el escáner, así el resto del sistema no arrastra su peso.
 *
 * Los tres parámetros de abajo son los que hacen que una etiqueta impresa se
 * lea de verdad; con los valores por defecto de ZXing no se lee nunca:
 *
 * - `TRY_HARDER`: sin este hint, OneDReader muestrea sólo 15 filas del centro
 *   de la imagen. Con el hint recorre el alto completo, que es lo que hace
 *   falta cuando el operario no centra la etiqueta con precisión.
 * - Un intento cada 100 ms (ZXing trae 500 ms): diez oportunidades por
 *   segundo de agarrar un cuadro nítido.
 * - Resolución: sin pedirla, el navegador entrega 640×480 y las barras de un
 *   Code 128 denso quedan por debajo de un píxel.
 *
 * Siempre queda disponible la carga manual: si el depósito no dio permiso de
 * cámara, o la etiqueta está rota, el operario tipea el código igual.
 */
export default function Escaner({
  onLeer,
  onCerrar,
  origen,
}: {
  onLeer: (codigo: string) => void;
  onCerrar: () => void;
  /** Centro del botón que lo abrió, en coordenadas de la pantalla. */
  origen?: { x: number; y: number };
}) {
  const video = useRef<HTMLVideoElement>(null);
  const pistaRef = useRef<MediaStreamTrack | null>(null);
  const detener = useRef<(() => void) | null>(null);
  const zoomDigital = useRef(1);
  const [error, setError] = useState('');
  const [manual, setManual] = useState('');
  const [iniciando, setIniciando] = useState(true);
  const [linterna, setLinterna] = useState<boolean | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rangoZoom, setRangoZoom] = useState<{ min: number; max: number; step: number; camara: boolean } | null>(null);
  // iPhone: Safari no deja usar el zoom óptico, pero cada lente trasera es una
  // cámara aparte. `lentes` guarda la principal y el teleobjetivo, si lo hay.
  const [lentes, setLentes] = useState<{ normal: string; tele: string } | null>(null);
  const [camaraId, setCamaraId] = useState<string | undefined>(undefined);

  // La cámara se abre una vez por lente elegida: si el efecto dependiera de
  // `onLeer` (que los padres pasan como función nueva en cada render) se
  // reiniciaría sola.
  const alLeer = useRef(onLeer);
  alLeer.current = onLeer;

  useEffect(() => {
    let vivo = true;

    async function abrirCamara() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Este dispositivo no expone la cámara al navegador. Cargá el código a mano.');
        setIniciando(false);
        return;
      }

      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ]);
        if (!vivo) return;

        // Las etiquetas del depósito son de una dimensión; QR entra por si
        // alguna etiqueta interna lo usa. Acotar la lista acelera cada intento.
        //
        // ITF y CODABAR quedaron afuera a propósito: no tienen dígito
        // verificador, así que un CODE 128 mal enfocado —barras pegadas, un
        // borde de la foto que parece barra— les puede dar una lectura válida
        // que es un número distinto del que dice la etiqueta. Un código
        // equivocado que el sistema acepta es peor que uno que no se lee: lo
        // segundo se vuelve a intentar, lo primero mueve mercadería. Nada de
        // lo que imprime el depósito usa esas dos simbologías.
        const hints = new Map<number, unknown>([
          [DecodeHintType.TRY_HARDER, true],
          [
            DecodeHintType.POSSIBLE_FORMATS,
            [
              BarcodeFormat.CODE_128,
              BarcodeFormat.CODE_39,
              BarcodeFormat.CODE_93,
              BarcodeFormat.EAN_13,
              BarcodeFormat.EAN_8,
              BarcodeFormat.QR_CODE,
            ],
          ],
        ]);

        const lector = new BrowserMultiFormatReader(hints as never);

        const flujo = await navigator.mediaDevices.getUserMedia({
          video: {
            // La cámara trasera es la que apunta a la etiqueta del pallet.
            ...(camaraId ? { deviceId: { exact: camaraId } } : { facingMode: { ideal: 'environment' } }),
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (!vivo) {
          flujo.getTracks().forEach((t) => t.stop());
          return;
        }
        const v = video.current!;
        v.srcObject = flujo;
        await v.play().catch(() => {});

        const [pista] = flujo.getVideoTracks();
        if (!pista) throw new DOMException('sin cámara', 'NotFoundError');
        pistaRef.current = pista;
        detener.current = () => {
          window.clearTimeout(timer);
          flujo.getTracks().forEach((t) => t.stop());
        };
        const cap = (pista.getCapabilities?.() ?? {}) as { zoom?: { min: number; max: number; step?: number }; torch?: boolean };
        setRangoZoom(
          cap.zoom && cap.zoom.max > cap.zoom.min
            ? { min: cap.zoom.min, max: cap.zoom.max, step: cap.zoom.step || 0.1, camara: true }
            : { min: 1, max: 4, step: 0.1, camara: false },
        );
        if (cap.zoom) setZoom(cap.zoom.min);
        setLinterna(cap.torch ? false : null);

        // Sin zoom de cámara (iPhone): se busca el teleobjetivo entre las
        // cámaras. Los nombres sólo se ven después de dar permiso, y vienen en
        // el idioma del equipo («Back Telephoto Camera», «…teleobjetivo…»).
        if (!camaraId && !cap.zoom) {
          const tele = (await navigator.mediaDevices.enumerateDevices()).find(
            (d) => d.kind === 'videoinput' && /tele/i.test(d.label),
          );
          const normal = pista.getSettings().deviceId;
          if (vivo && tele && normal && tele.deviceId !== normal) setLentes({ normal, tele: tele.deviceId });
        }

        // Sólo se decodifica lo que está dentro de la guía verde: otra etiqueta
        // que asome en el borde del cuadro no se puede leer por error.
        const lienzo = document.createElement('canvas');
        const ctx = lienzo.getContext('2d', { willReadFrequently: true })!;
        let timer = 0;
        const buscar = () => {
          if (!vivo) return;
          if (v.videoWidth && v.clientWidth) {
            const r = recorteGuia(
              { ancho: v.videoWidth, alto: v.videoHeight },
              { ancho: v.clientWidth, alto: v.clientHeight },
              zoomDigital.current,
            );
            lienzo.width = r.w;
            lienzo.height = r.h;
            ctx.drawImage(v, r.x, r.y, r.w, r.h, 0, 0, r.w, r.h);
            try {
              const texto = lector.decodeFromCanvas(lienzo).getText();
              vivo = false;
              detener.current?.();
              // Con guantes no se ve bien la pantalla: la vibración confirma la lectura al tacto.
              navigator.vibrate?.(80);
              alLeer.current(texto.trim().toUpperCase());
              return;
            } catch {
              // Nada legible en este cuadro: se prueba con el siguiente.
            }
          }
          timer = window.setTimeout(buscar, 100);
        };
        buscar();
        setIniciando(false);
      } catch (err) {
        if (!vivo) return;
        const nombre = err instanceof Error ? err.name : '';
        setError(
          nombre === 'NotAllowedError'
            ? 'No nos diste permiso para usar la cámara. Habilitalo en el navegador o cargá el código a mano.'
            : nombre === 'NotFoundError'
              ? 'No encontramos ninguna cámara en el dispositivo.'
              : 'No pudimos abrir la cámara. Cargá el código a mano.',
        );
        setIniciando(false);
      }
    }

    void abrirCamara();

    return () => {
      vivo = false;
      detener.current?.();
    };
  }, [camaraId]);

  function cambiarLente(id: string) {
    if (id === camaraId) return;
    setIniciando(true);
    setLinterna(null);
    setCamaraId(id);
  }

  // El zoom de la cámara suma resolución real; si el equipo no lo tiene, se
  // agranda la imagen y la guía pasa a cubrir menos píxeles del cuadro.
  function cambiarZoom(z: number) {
    setZoom(z);
    if (rangoZoom?.camara) {
      void pistaRef.current?.applyConstraints({ advanced: [{ zoom: z } as MediaTrackConstraintSet] }).catch(() => {});
    } else {
      zoomDigital.current = z;
    }
  }

  const cerrar = useCallback(() => onCerrar(), [onCerrar]);
  const celular = useEsCelular();

  // Escape cierra, como cualquier modal del sistema.
  useEscape(true, cerrar);

  async function alternarLinterna() {
    const pista = pistaRef.current;
    if (!pista || linterna === null) return;
    try {
      await pista.applyConstraints({ advanced: [{ torch: !linterna } as MediaTrackConstraintSet] });
      setLinterna(!linterna);
    } catch {
      setLinterna(null); // el dispositivo no la soporta de verdad
    }
  }

  function enviarManual() {
    const codigo = manual.trim().toUpperCase();
    if (codigo) alLeer.current(codigo);
  }

  // El velo se funde y el panel sube desde abajo en el celular (es donde
  // está apoyado) o crece en el lugar en PC. Si quien lo abre lo envuelve en
  // AnimatePresence, también sale animado.
  // Con `origen` (el botón de la cámara que lo abrió) el velo crece en círculo
  // desde ese botón hasta tapar la pantalla: la cámara «se abre» desde ahí.
  const entrada = origen
    ? revelarDesde(`${origen.x}px`, `${origen.y}px`)
    : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: suave };
  // Desde un botón, el panel espera a que el círculo ya tape casi todo: si
  // subiera junto con el velo, aparecería recortado por el borde del círculo.
  const panel = celular ? subir : pop;
  const llegada = origen ? { ...panel, transition: { ...panel.transition, delay: 0.16 } } : panel;
  return (
    <motion.div
      {...entrada}
      role="dialog"
      aria-modal="true"
      aria-label="Escanear etiqueta"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(26,26,26,.72)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 12,
      }}
      onClick={(e) => e.target === e.currentTarget && cerrar()}
    >
      <motion.div
        {...llegada}
        style={{
          width: '100%',
          maxWidth: 460,
          background: c.panel,
          borderRadius: 20,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '92vh',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
          <Icono nombre="barcode_scanner" size={22} color={c.verde} />
          <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: c.texto }}>Escanear etiqueta</span>

          {linterna !== null && (
            <button
              type="button"
              onClick={alternarLinterna}
              aria-label={linterna ? 'Apagar la linterna' : 'Encender la linterna'}
              aria-pressed={linterna}
              style={{
                width: 44,
                height: 44,
                flex: '0 0 44px',
                borderRadius: 999,
                border: `1px solid ${linterna ? c.verde : c.borde}`,
                background: linterna ? c.verdeSuave : c.panel,
                color: linterna ? c.verde : c.texto2,
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Icono nombre={linterna ? 'flashlight_on' : 'flashlight_off'} size={20} />
            </button>
          )}

          <button
            type="button"
            onClick={cerrar}
            aria-label="Cerrar el escáner"
            style={{
              width: 44,
              height: 44,
              flex: '0 0 44px',
              borderRadius: 999,
              border: `1px solid ${c.borde}`,
              background: c.panel,
              color: c.texto2,
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            <Icono nombre="close" size={20} />
          </button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          {!error && (
            <div
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '4 / 3',
                borderRadius: 16,
                overflow: 'hidden',
                background: '#1a1a1a',
              }}
            >
              <video
                ref={video}
                muted
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  transform: rangoZoom && !rangoZoom.camara ? `scale(${zoom})` : undefined,
                }}
              />
              {/* Guía ancha y baja: un código de barras es apaisado. Sólo se lee lo de adentro. */}
              <span
                style={{
                  position: 'absolute',
                  left: `${GUIA.izq * 100}%`,
                  right: `${GUIA.der * 100}%`,
                  top: `${GUIA.arriba * 100}%`,
                  bottom: `${GUIA.abajo * 100}%`,
                  border: `2px solid ${c.verde}`,
                  borderRadius: 10,
                  boxShadow: '0 0 0 9999px rgba(26,26,26,.24)',
                }}
              />
              {/* Se funde cuando la imagen ya está: la cámara aparece debajo, no de golpe. */}
              <AnimatePresence>
                {iniciando && (
                  <motion.span
                    key="iniciando"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={suave}
                    className="wms-cargando"
                    style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 13.5 }}
                  >
                    Abriendo la cámara…
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          )}

          {!error && lentes && (
            <motion.div {...aparecer} role="group" aria-label="Lente" style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              {[
                { id: lentes.normal, texto: '1×' },
                { id: lentes.tele, texto: 'Tele' },
              ].map((l) => {
                const activa = (camaraId ?? lentes.normal) === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => cambiarLente(l.id)}
                    aria-pressed={activa}
                    style={{
                      minWidth: 64,
                      minHeight: 44,
                      borderRadius: 999,
                      border: `1px solid ${activa ? c.verde : c.borde}`,
                      background: activa ? c.verdeSuave : c.panel,
                      color: activa ? c.verde : c.texto2,
                      fontFamily: mono,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {l.texto}
                  </button>
                );
              })}
            </motion.div>
          )}

          {!error && rangoZoom && (
            <motion.label {...aparecer} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44 }}>
              <Icono nombre="zoom_in" size={20} color={c.texto2} />
              <input
                type="range"
                min={rangoZoom.min}
                max={rangoZoom.max}
                step={rangoZoom.step}
                value={zoom}
                onChange={(e) => cambiarZoom(Number(e.target.value))}
                aria-label="Zoom de la cámara"
                style={{ flex: 1, accentColor: c.verde }}
              />
              <span style={{ fontFamily: mono, fontSize: 12, color: c.texto3, minWidth: 36, textAlign: 'right' }}>
                {zoom.toFixed(1)}×
              </span>
            </motion.label>
          )}

          {error ? (
            <motion.div
              {...abrir(16)}
              style={{
                padding: '12px 14px',
                borderRadius: 16,
                background: c.ambarSuave,
                border: `1px solid ${c.ambar}`,
                fontSize: 13,
                color: c.texto,
                textWrap: 'pretty',
              }}
            >
              {error}
            </motion.div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              <div style={{ fontSize: 13, color: c.texto2, textAlign: 'center', textWrap: 'pretty' }}>
                Acercá el código hasta que ocupe el ancho del recuadro. Se lee solo.
              </div>
              {/* Deja ver que el lector está trabajando, sin números técnicos */}
              {!iniciando && (
                <motion.div {...aparecer} style={{ fontFamily: mono, fontSize: 12, color: c.texto4 }}>Buscando el código…</motion.div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1, height: 1, background: c.borde }} />
            <span style={{ fontSize: 12, color: c.texto3 }}>o cargalo a mano</span>
            <span style={{ flex: 1, height: 1, background: c.borde }} />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              type="text"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  enviarManual();
                }
              }}
              placeholder="PLT-004821"
              aria-label="Código de la etiqueta"
              autoFocus={Boolean(error)}
              style={{
                flex: 1,
                minWidth: 160,
                height: 48,
                padding: '0 16px',
                border: `1px solid ${c.bordeFuerte}`,
                borderRadius: 999,
                background: c.panel,
                fontFamily: mono,
                fontSize: 15,
                color: c.texto,
              }}
            />
            <button
              type="button"
              onClick={enviarManual}
              disabled={!manual.trim()}
              style={{ ...botonPrimario, minHeight: 48, opacity: manual.trim() ? 1 : 0.5 }}
            >
              Buscar
            </button>
          </div>

          <button type="button" onClick={cerrar} style={{ ...botonSecundario, minHeight: 44, justifyContent: 'center' }}>
            Cancelar
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
